import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { APP_ROUTES } from "../constants";
import {
  getWidgetLinkId,
  probeLinkSession,
  USER_BLOCKED_EVENT,
} from "../services/api";
import { resolveXLinkIdHeader } from "../services/resolveXLinkIdHeader";
import { useSessionStore } from "../store/useSessionStore";
import { RequestStatus } from "../utils/commons";
import { shouldSkipLinkLivenessProbe } from "../utils/linkLivenessProbe";
import { resolveUrlLinkId } from "../utils/resolveUrlLinkId";
import { useRouteWithToken } from "./useRouteWithToken";

const DEAD_LINK_PATHS = [
  APP_ROUTES.SESSION_EXPIRED_PAGE,
  APP_ROUTES.ERROR_PAGE,
  APP_ROUTES.MAINTENANCE_PAGE,
  APP_ROUTES.SUPPORT_PAGE,
] as const;

function resolveProbeLinkId(): string | null {
  return resolveXLinkIdHeader({
    urlLinkId: resolveUrlLinkId(),
    currentLinkId: getWidgetLinkId(),
    persistedLinkId: useSessionStore.getState().session.data?.linkId ?? null,
  });
}

/**
 * On tab resume / BFCache restore, silently GET the link session. Live = no-op.
 * Dead = interceptor + `useSessionExpiredHandler`. Network = fail-open.
 * Blocked (claim or probe) → ErrorPage + support. Suspended is not ejected.
 */
export function useTabResumeLinkLiveness() {
  const { t } = useTranslation();
  const location = useLocation();
  const { redirectTo } = useRouteWithToken();
  const blocked = useSessionStore(
    (state) => state.session.data?.userStatus?.blocked === true
  );
  const pathnameRef = useRef(location.pathname);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const probeInFlightRef = useRef(false);
  const lastAttemptAtRef = useRef<number | null>(null);
  const wasHiddenRef = useRef(false);

  const redirectBlockedUser = useCallback(() => {
    if (
      pathnameRef.current === APP_ROUTES.ERROR_PAGE ||
      pathnameRef.current === APP_ROUTES.SUPPORT_PAGE ||
      pathnameRef.current === APP_ROUTES.SESSION_EXPIRED_PAGE
    ) {
      return;
    }
    redirectTo(APP_ROUTES.ERROR_PAGE, {
      replace: true,
      state: {
        title: t("error.transfersDisabledTitle"),
        subTitle: null,
        message: t("error.transfersDisabledMessage"),
        btnText: t("common.needHelp"),
        isOut: false,
        pageUrl: APP_ROUTES.SUPPORT_PAGE,
      },
    });
  }, [redirectTo, t]);

  useEffect(() => {
    if (blocked) {
      redirectBlockedUser();
    }
  }, [blocked, location.pathname, redirectBlockedUser]);

  useEffect(() => {
    window.addEventListener(USER_BLOCKED_EVENT, redirectBlockedUser);
    return () => {
      window.removeEventListener(USER_BLOCKED_EVENT, redirectBlockedUser);
    };
  }, [redirectBlockedUser]);

  useEffect(() => {
    const runProbe = () => {
      const linkId = resolveProbeLinkId();
      const skip = shouldSkipLinkLivenessProbe({
        linkId,
        pathname: pathnameRef.current,
        deadLinkPaths: DEAD_LINK_PATHS,
        claimInFlight:
          useSessionStore.getState().session.status === RequestStatus.LOADING,
        probeInFlight: probeInFlightRef.current,
        lastAttemptAt: lastAttemptAtRef.current,
        now: Date.now(),
      });
      if (skip || !linkId) return;

      lastAttemptAtRef.current = Date.now();
      probeInFlightRef.current = true;
      void probeLinkSession(linkId)
        .then((session) => {
          if (session.blocked) {
            redirectBlockedUser();
          }
        })
        .catch(() => {
          // Fail-open: interceptor already routed dead links.
        })
        .finally(() => {
          probeInFlightRef.current = false;
        });
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        wasHiddenRef.current = true;
        return;
      }
      if (document.visibilityState === "visible" && wasHiddenRef.current) {
        runProbe();
      }
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        runProbe();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [redirectBlockedUser]);
}
